// source: https://docs.google.com/spreadsheets/d/1QokQSvG5rI25jgKM09cqaBV6jUNH6IwOrU1i3PDzG-g/edit#gid=644521353

import { amorphicStatic, supertypeClass } from '@haventech/amorphic';
import { documents as RECDocuments } from './casrec-app-package/state-map-default/icc-state';
import { documents as REDocuments } from './casre-app-package/state-map-default/icc-state';
import { documents as SVDocuments } from './cassv-app-package/state-map-default/icc-state';
import { PolicyDoc } from '../../../../common/js/customer/PolicyDoc';
import { AnnuityAdditionalSigners, AnnuityAppDocument, AnnuityProductSelection } from '../../interfaces/CoverpathTypes';
import { CoverpathUtilsBackEnd } from '../CoverpathUtilsBackEnd';
import { CoverpathAnnuity } from '../../../classes/coverpath_subclasses/CoverpathAnnuity';
import { POLICY_DOC_ANNUITY_DOCTYPE } from '../../../../common/js/constants/policy-doc';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../../common/js/logger/LoggerSingleton';

export type AnnuityAppDocs = {
  [docType: string]: AnnuityAppDocument;
};

@supertypeClass({ toClient: false })
export class AnnuityDocuments {
  static SHARED_AGENT_ESIGN_DOCUMENTS = [
    PolicyDoc.DOCTYPE.annuityApplication,
    PolicyDoc.DOCTYPE.annuitySuitabilityForm,
    PolicyDoc.DOCTYPE.annuityReplacementForm,
  ];

  static CASREC_AGENT_ESIGN_DOCUMENTS = [PolicyDoc.DOCTYPE.annuityProductDisclosure];

  static CASRE_AGENT_ESIGN_DOCUMENTS = [];

  static CASSV_AGENT_ESIGN_DOCUMENTS = [];

  static CLIENT_ESIGN_STATIC_DOCUMENTS = [
    PolicyDoc.DOCTYPE.annuityADARForm,
    PolicyDoc.DOCTYPE.annuityProductSnapshot,
    PolicyDoc.DOCTYPE.annuityPrivacyPolicy,
    PolicyDoc.DOCTYPE.annuityRECEConsent,
    PolicyDoc.DOCTYPE.annuityIllustration,
    PolicyDoc.DOCTYPE.annuityBuyerGuide,
    PolicyDoc.DOCTYPE.annuityStateSupplemental,
    PolicyDoc.DOCTYPE.annuityProducerTransactionAuthorization,
    PolicyDoc.DOCTYPE.annuityWithdrawalBenefitRider,
    PolicyDoc.DOCTYPE.annuityREEConsent,
    PolicyDoc.DOCTYPE.annuityBeneficiaryIRA,
  ];

  static PA_APP_PACKET_DOCUMENTS = [
    PolicyDoc.DOCTYPE.annuityApplication,
    PolicyDoc.DOCTYPE.annuitySuitabilityForm,
    PolicyDoc.DOCTYPE.annuityReplacementForm,
    PolicyDoc.DOCTYPE.annuityCertificateOfTrustForm,
    PolicyDoc.DOCTYPE.annuityProductSnapshot,
    PolicyDoc.DOCTYPE.annuityPrivacyPolicy,
    PolicyDoc.DOCTYPE.annuityProductDisclosure,
    PolicyDoc.DOCTYPE.annuityRECEConsent,
    PolicyDoc.DOCTYPE.annuityIllustration,
    PolicyDoc.DOCTYPE.annuityBuyerGuide,
    PolicyDoc.DOCTYPE.annuityStateSupplemental,
    PolicyDoc.DOCTYPE.annuityADARForm,
    PolicyDoc.DOCTYPE.annuityQLACForm,
    PolicyDoc.DOCTYPE.annuityMM2Disclosure,
    PolicyDoc.DOCTYPE.annuityWithdrawalBenefitRider,
    PolicyDoc.DOCTYPE.annuityREEConsent,
    PolicyDoc.DOCTYPE.annuityBeneficiaryIRA,
    PolicyDoc.DOCTYPE.annuityLargeCaseQuestionnaire,
    PolicyDoc.DOCTYPE.annuityQualifiedPlanRollover,
    PolicyDoc.DOCTYPE.annuityDisclosureAcknowledgment,
  ];

  public static async fetchDocsForAgentEsign(annuity: CoverpathAnnuity) {
    const policyDocs = await CoverpathUtilsBackEnd.fetchPolicyDocs(annuity);
    const unorderedDocTypes = policyDocs.map((policyDoc) => policyDoc.appType);
    const docTypes = Object.values(POLICY_DOC_ANNUITY_DOCTYPE).filter((docType) => unorderedDocTypes.includes(docType));

    const docInfos = await Promise.all(
      docTypes.map((docType) => AnnuityDocuments.getAnnuityDoc(annuity.product, annuity.getApplicationState(), docType))
    );

    return docInfos
      .filter((docInfos) => docInfos && !docInfos.isEmpty && docInfos.requiresAdvisorSignature)
      .map((docInfo) => {
        return {
          displayName: docInfo.displayName,
          documentName: docInfo.type,
          triggerAction: `viewAnnuityDocument=true&type=${docInfo.type}&annuityId=${annuity._id}`,
          isActive: false,
          docType: docInfo.type,
        };
      });
  }

  public static async fetchDocsForClientEsign(
    policyDocs: PolicyDoc[],
    product: AnnuityProductSelection,
    legalState: string,
    urlPath: string
  ) {
    const savedPolicyDocTypes = policyDocs.map((policyDoc) => policyDoc.appType);
    const docsToInclude = AnnuityDocuments.CLIENT_ESIGN_STATIC_DOCUMENTS.filter((appType) =>
      savedPolicyDocTypes.includes(appType)
    );
    // @NOTE: we are adding Illustration entry even though it does not exist in DB
    // This allows CP-Client site to display error message during client e-signature process
    if (!docsToInclude.includes(PolicyDoc.DOCTYPE.annuityIllustration)) {
      docsToInclude.push(PolicyDoc.DOCTYPE.annuityIllustration);
    }

    const documentMap = await AnnuityDocuments.getDocumentsForState(product, legalState);
    return docsToInclude
      .map((appType) => documentMap[appType])
      .map((doc) => ({
        displayName: doc.displayName,
        pdfUrl: `amorphic/xhr?path=${urlPath}&file=true&viewAnnuityDocument=true&type=${doc.type}`,
      }));
  }

  public static async fetchDocsForAdditionalEsign(
    allPolicyDocs: any,
    product: AnnuityProductSelection,
    legalState: string,
    recipient: AnnuityAdditionalSigners,
    urlpath: string,
    jwtToken: string
  ) {
    const documentMap = await AnnuityDocuments.getDocumentsForState(product, legalState);
    const policyDocsByAppType = allPolicyDocs.reduce((map, policyDoc) => {
      map.set(policyDoc.appType, policyDoc);
      return map;
    }, new Map());

    const formsToSign = Object.keys(documentMap)
      .map((appType) => {
        const policyDoc = policyDocsByAppType.get(appType);
        if (policyDoc) {
          const doc = documentMap[appType];
          const isSignatureRequired =
            (recipient === 'jointowner' && !!doc.requiresJointOwnerSignature) ||
            (recipient === 'primaryannuitant' && !!doc.requiresAnnuitantSignature) ||
            (recipient === 'jointannuitant' && !!doc.requiresJointAnnuitantSignature);
          return {
            ...doc,
            formType: doc.displayName,
            pdfUrl: `amorphic/xhr?path=${urlpath}&file=true&annuity=true&appType=${appType}&jwtToken=${jwtToken}`,
            textPrompt: ``,
            eSignLabel: isSignatureRequired ? `Esign ${doc.displayName}` : 'Continue',
            isSignatureRequired,
            appType,
          };
        }
      })
      .filter(Boolean);

    if (!formsToSign[formsToSign.length - 1].isSignatureRequired) {
      formsToSign[formsToSign.length - 1].eSignLabel = 'Submit';
    }

    return formsToSign;
  }

  public static async fetchDocsForPolicyAdmin(annuity: CoverpathAnnuity) {
    const docsToInclude = AnnuityDocuments.PA_APP_PACKET_DOCUMENTS;
    const pdfsToMerge = [];

    for (let i = 0; i < docsToInclude.length; i++) {
      const docToInclude = docsToInclude[i];
      const policyDoc = await CoverpathUtilsBackEnd.fetchSinglePolicyDoc(annuity, docToInclude);

      if (!!policyDoc) {
        LoggerSingleton.getInstance().info(
          {
            module: 'AnnuityDocuments',
            function: 'fetchDocsForPolicyAdmin',
            data: {
              contractNumber: annuity.contractNumber,
              product: annuity.product,
              docToInclude,
            },
            category: Category.request,
          },
          `Adding to policy packet`
        );

        pdfsToMerge.push(policyDoc.docContent.toString());
      } else {
        //Note: Not all of these forms are strictly necessary for all states
        LoggerSingleton.getInstance().info(
          {
            module: 'AnnuityDocuments',
            function: 'fetchDocsForPolicyAdmin',
            data: {
              contractNumber: annuity.contractNumber,
              product: annuity.product,
              docToInclude,
            },
            category: Category.request,
          },
          `Skipping; Could not find document for policy packet`
        );
      }
    }

    return pdfsToMerge;
  }

  public static async getAnnuityDoc(product: AnnuityProductSelection, legalState: string, docType: string) {
    const legalStateDocumentMap = await AnnuityDocuments.getDocumentsForState(product, legalState);
    return legalStateDocumentMap[docType];
  }

  public static async getDocumentsForState(product: AnnuityProductSelection, state: string): Promise<AnnuityAppDocs> {
    let iccStateDocuments;
    if (product === AnnuityProductSelection.CASREC) {
      iccStateDocuments = await RECDocuments();
    } else if (product === AnnuityProductSelection.CASRE) {
      iccStateDocuments = await REDocuments();
    } else if (product === AnnuityProductSelection.CASSV) {
      iccStateDocuments = await SVDocuments();
    }
    const stateDocs = await AnnuityDocuments.getStateDocs(product, state);
    return {
      ...iccStateDocuments,
      ...stateDocs.documents, // override icc version
    };
  }

  private static async getStateDocs(product: AnnuityProductSelection, state: string) {
    const path = `./${product.toLocaleLowerCase()}-app-package/state-map/${state}`;
    if (AnnuityDocuments.isModuleAvailable(path)) {
      return import(path);
    } else {
      return {};
    }
  }

  private static isModuleAvailable(path: string) {
    try {
      require.resolve(path);
      return true;
    } catch (e) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'AnnuityDocuments.ts',
          function: 'iSmoduleAvailable',
          category: Category.request,
        },
        `Document at path ${path} not found`
      );
      return false;
    }
  }
}
